import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useForm from 'react-hook-form';
import PropTypes from 'prop-types';
import Select from 'react-select';
import InputMask from 'react-input-mask';
import { Switch } from 'pretty-checkbox-react';
/* actions */
import { getAllCompanies, editCompanies } from '../../redux/actions/companies';
/* components */
import ErrorMsgField from '../../components/ErrorMsgField';
import AlertMsg from '../../components/AlertMsg';
import FormFooter from '../../components/FormFooter';
import FormHeader from '../../components/FormHeader';
/* form-validation */
import schema from '../../form-validation/company';
/* utils */
import {
  customStyle,
  customThemes,
  customFilter,
  firstOption,
  handleChangeSelect
} from '../../utils/select';
import sortable from '../../utils/sortable';
import handleChangeDefault from '../../utils/handleChangeDefault';
import defaultPermissions, {
  idCompany4Case
} from '../../utils/defaultPermissions';

function Edit (props) {
  const { id } = props.match.params;
  const INITIAL_VALUES = {
    _id: id || '',
    name: '',
    cnpj: '',
    type: 'M',
    headOffice: '',
    description: '',
    permissions: [...defaultPermissions],
    licenses: {
      user: 0,
      ipRecorder: 0
    }
  };
  const dispatch = useDispatch();
  const submitButton = useRef();
  const { register, handleSubmit, errors, setError, setValue } = useForm({
    validationSchema: schema,
    reValidateMode: 'onSubmit'
  });
  const companies = useSelector(state => state.companies);
  const company = companies.data.find(item => item._id === id);
  const [data, setData] = useState(INITIAL_VALUES);
  const [headOfficeOptions, setHeadOfficeOptions] = useState([firstOption]);

  useEffect(() => {
    dispatch(getAllCompanies());
  }, [dispatch]);

  useEffect(() => {
    if (company) {
      // Código para comparar se o sidebar que está
      // no banco é o atual do sistema

      const { permissions } = INITIAL_VALUES;

      const guides = permissions.map(guide => guide.id);

      const menus = permissions.reduce((obj, guide) => {
        obj[guide.id] = guide.items ? guide.items.map(menu => menu.id) : [];
        return obj;
      }, {});

      const submenus = permissions.reduce((obj, guide) => {
        obj[guide.id] = guide.items
          ? guide.items.reduce((obj, menu) => {
            obj[menu.id] = menu.items
              ? menu.items.map(submenu => submenu.id)
              : [];
            return obj;
          }, {})
          : {};
        return obj;
      }, {});

      const newGuides = guides
        .map(nameGuide => {
          // Atualiza as guias
          const _guide = company.permissions.find(
            guide => guide.id === nameGuide
          );
          const newGuide = !_guide
            ? permissions.find(guide => guide.id === nameGuide)
            : _guide;
          // Atualiza os menus
          if (newGuide.items && newGuide.items.length) {
            const _menu = menus[nameGuide].map(nameMenu => {
              const newMenu = newGuide.items.find(menu => menu.id === nameMenu);
              const _newMenu = !newMenu
                ? permissions
                  .find(guide => guide.id === nameGuide)
                  .items.find(menu => menu.id === nameMenu)
                : newMenu;
              const _submenu = submenus[nameGuide][nameMenu].map(
                nameSubmenu => {
                  const newSubmenu = _newMenu.items.find(
                    submenu => submenu.id === nameSubmenu
                  );
                  if (!newSubmenu) {
                    const obj = permissions
                      .find(guide => guide.id === nameGuide)
                      .items.find(menu => menu.id === nameMenu)
                      .items.find(submenu => submenu.id === nameSubmenu);
                    return { ...obj };
                  }
                  return { ...newSubmenu };
                }
              );

              return { ..._newMenu, items: [..._submenu] };
            });
            // Guia e Menu Atualizados
            const newObj = { ...newGuide, items: [..._menu] };
            return newObj;
            // Atualizando Submenu
          }
          return { ...newGuide };
        })
        .filter(item => !!item);

      const newData = {
        ...INITIAL_VALUES,
        ...company,
        headOffice: company.headOffice ? company.headOffice._id : '',
        permissions: [...newGuides]
      };
      setData(newData);
    }
  }, [company]);

  useEffect(() => {
    if (companies.data.length) {
      const options = companies.data
        .filter(company => company.type === 'M')
        .map(company => ({
          value: company._id,
          label: `${company.name}`,
          sortValue: `${company.name}`
        }))
        .sort(sortable);

      options.unshift(firstOption);
      setHeadOfficeOptions([...options]);
    }
  }, [companies.data, setHeadOfficeOptions]);

  const submit = () => {
    const newData = {
      ...data,
      headOffice: data.type === 'F' ? data.headOffice : null
    };
    dispatch(editCompanies(newData, submitButton.current));
  };

  const handleChangeCNPJ = evt => {
    const { name, value } = evt.target;
    setData({ ...data, [name]: value });
    if (/^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/.test(value)) {
      setError(name);
    }
  };

  const handleChangeType = evt => {
    const { name, value } = evt.target;
    if (value === 'M') {
      setData({ ...data, [name]: value, headOffice: '' });
    } else {
      setData({ ...data, [name]: value });
    }
  };

  const handleChangeGuides = evt => {
    const { name, checked } = evt.target;
    const permissions = data.permissions.map(guide => {
      if (guide.id === name) {
        return { ...guide, enabled: checked };
      }
      return { ...guide };
    });
    setData({ ...data, permissions });
  };

  const handleChangeMenu = (evt, guide) => {
    const { name, checked } = evt.target;
    const permissions = data.permissions.map(_guide => {
      if (_guide.id === guide.id && guide.items && guide.enabled) {
        _guide.items = guide.items.map(menu => {
          if (menu.id === name) {
            return { ...menu, enabled: checked };
          }
          return { ...menu };
        });
      }
      return { ..._guide };
    });
    setData({ ...data, permissions });
  };

  const handleChangeSubMenu = (evt, guide, menu) => {
    const { name, checked } = evt.target;
    const permissions = data.permissions.map(_guide => {
      if (_guide.id === guide.id && guide.items && guide.enabled) {
        _guide.items = guide.items.map(_menu => {
          if (_menu.id === menu.id && menu.items && menu.enabled) {
            _menu.items = menu.items.map(submenu => {
              if (submenu.id === name) {
                return { ...submenu, enabled: checked };
              }
              return { ...submenu };
            });
          }
          return { ..._menu };
        });
      }
      return { ..._guide };
    });
    setData({ ...data, permissions });
  };

  const handleChangeLicenses = (evt, nameField) => {
    evt.preventDefault();
    const { value } = evt.target;
    if (!isNaN(value) || value === '') {
      const licenses = { ...data.licenses, [nameField]: value };
      setData({ ...data, licenses });
    } else {
      evt.target.value = data.licenses[nameField];
    }
  };

  return (
    <div className="container-fluid">
      {companies.editSuccess && (
        <AlertMsg type="success" msg="Empresa atualizada com sucesso" />
      )}
      {companies.errorContent &&
        !companies.errorContent.message.includes('400') &&
        companies.editError && (
        <AlertMsg type="danger" msg="Falha durante atualização da empresa" />
      )}
      {companies.errorContent &&
        companies.errorContent.message.includes('400') &&
        companies.editError && (
        <AlertMsg
          type="danger"
          msg="Uma empresa com o CNPJ informado já está cadastrado"
        />
      )}
      {!companies.editSuccess && !companies.editError && (
        <AlertMsg type="info" msg="Área atualização de empresas" />
      )}
      <FormHeader title="Atualizar Empresa" />
      <form onSubmit={handleSubmit(submit)}>
        <div className="row">
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="name">
                Nome<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="name"
                name="name"
                autoComplete="off"
                defaultValue={data.name}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.name && errors.name.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="cnpj">
                CNPJ<span className="field-required">*</span>
              </label>
              <InputMask
                id="cnpj"
                name="cnpj"
                mask="99.999.999/9999-99"
                className="form-control"
                autoComplete="off"
                placeholder="__.___.___/____-__"
                value={data.cnpj}
                onChange={handleChangeCNPJ}
                ref={register}
              />
              {errors.cnpj && errors.cnpj.type === 'cnpj' && (
                <ErrorMsgField type="cnpj" />
              )}
            </div>
          </div>
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="type">
                Tipo<span className="field-required">*</span>
              </label>
              <select
                className="form-control"
                id="type"
                name="type"
                onChange={handleChangeType}
                value={data.type}
                ref={register}
              >
                <option value="M">Matriz</option>
                <option value="F">Filial</option>
              </select>
              {errors.type && errors.type.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-3">
            <div className="form-group">
              <div className="form-group form-inline mb-0">
                <label
                  className="col-sm-12 d-inline p-0 mb-2"
                  htmlFor="headOffice"
                >
                  Sede
                </label>
                <Select
                  className="col p-0"
                  name="headOffice"
                  ref={register}
                  onChange={evt =>
                    handleChangeSelect(
                      { ...evt, name: 'headOffice' },
                      data,
                      setData,
                      setError,
                      setValue
                    )
                  }
                  defaultValue={headOfficeOptions[0]}
                  value={headOfficeOptions.find(
                    headOffice => headOffice.value === data.headOffice
                  )}
                  options={headOfficeOptions}
                  isSearchable={true}
                  theme={customThemes}
                  styles={customStyle}
                  filterOption={customFilter}
                  noOptionsMessage={() => 'Nenhum registro encontrado'}
                  isDisabled={data.type === 'M'}
                />
              </div>
              {errors.headOffice && errors.headOffice.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="description">
            Descrição<span className="field-required">*</span>
          </label>
          <textarea
            className="form-control"
            id="description"
            name="description"
            rows="3"
            onChange={evt => handleChangeDefault(evt, data, setData)}
            defaultValue={data.description}
            ref={register}
          ></textarea>
          {errors.description && errors.description.type === 'required' && (
            <ErrorMsgField type="required" />
          )}
        </div>
        <div className="form-group mb-0">
          <label htmlFor="licenses">Licenciamento do Sistema</label>
        </div>
        <div className="row">
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="userLicenses">
                Qtd. de Usuários Logado<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="userLicenses"
                name="userLicenses"
                autoComplete="off"
                value={data.licenses.user}
                onChange={evt => handleChangeLicenses(evt, 'user')}
                ref={register}
              />
              {errors.userLicenses &&
                errors.userLicenses.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="ipRecorderLicenses">
                Qtd. de Ramais IP Gravado
                <span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="ipRecorderLicenses"
                name="ipRecorderLicenses"
                autoComplete="off"
                value={data.licenses.ipRecorder}
                onChange={evt => handleChangeLicenses(evt, 'ipRecorder')}
                ref={register}
              />
              {errors.ipRecorderLicenses &&
                errors.ipRecorderLicenses.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="permissions">
            Módulos Contratados<span className="field-required">*</span>
          </label>
          {data.permissions.map((guide, i) => {
            return (
              <div key={i} className="form-group pl-3">
                <div className="checkbox">
                  <Switch
                    type="checkbox"
                    color="success"
                    name={guide.id}
                    onChange={handleChangeGuides}
                    checked={guide.enabled}
                  ></Switch>
                  <span className="ml-2">
                    <i className={guide.icon} /> GUIA {guide.tab}
                  </span>
                </div>
                {guide.items &&
                  guide.items.map((menu, i) => {
                    return (
                      <div key={i} className="mb-1 pl-5">
                        <div className="checkbox">
                          <Switch
                            type="checkbox"
                            color="success"
                            name={menu.id}
                            onChange={evt => handleChangeMenu(evt, guide)}
                            checked={guide.enabled ? menu.enabled : false}
                          ></Switch>
                          <span className="ml-2">
                            <i className={menu.icon} /> {menu.title}
                          </span>
                        </div>
                        {menu.items &&
                          menu.items.map((submenu, i) => {
                            if (
                              submenu.id === 'companies' &&
                              data._id !== idCompany4Case
                            ) {
                              return false;
                            }
                            return (
                              <div key={i} className="mb-1 pl-4">
                                <div className="checkbox">
                                  <Switch
                                    type="checkbox"
                                    color="primary"
                                    name={submenu.id}
                                    onChange={evt =>
                                      handleChangeSubMenu(evt, guide, menu)
                                    }
                                    checked={
                                      guide.enabled && menu.enabled
                                        ? submenu.enabled
                                        : false
                                    }
                                  ></Switch>
                                  <span className="ml-2">
                                    <i className={submenu.icon} />{' '}
                                    {submenu.title}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    );
                  })}
              </div>
            );
          })}
        </div>
        <FormFooter url="/companies" reference={submitButton} />
      </form>
    </div>
  );
}

Edit.propTypes = {
  match: PropTypes.object
};

export default Edit;
